import { useIntl } from 'react-intl';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StyledDrawerViewListingApplication,
  StyledDrawerTitleWrapper,
  StyledFooter,
} from './style';
import DrawerSendMessage from './DrawerSendMessage';
import { DrawerWithdrawListingApplication } from './DrawerWithdrawListingApplication';
import {
  StyledApplicationForm,
  StyledApplicationSummary,
} from '../../Form/Listings/ListingApplication/style';
import { DrawerProps } from '../Drawer';
import { Label } from '../../Label';
import { Spinner } from '../../Spinner';
import { Button } from '../../Button';
import { Icon } from 'ui/Icon';
import { Tabs } from '../../Tabs';
import { Input } from '../../Input';
import { DatePicker } from '../../DatePicker';
import { Divider } from '../../Divider';
import { Form, message, Modal } from 'antd';
import { Checkbox } from 'ui/Checkbox';
import { StyledCustomUploadList } from 'ui/Upload/Uploader/style';
import { CustomDocument } from 'ui/Upload/Uploader';
import { UploadFile } from 'antd';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import {
  ListingApplication,
  Listing,
  ApplicationDeliveryCondition,
  ListingProduct,
  FarmQualityMark,
  QualityMarkId,
  ApplicationCertificate,
  FileMeta,
  ApplicationProductData,
  FarmProduct,
} from '../../../types/ListingData';
import { useAuth } from 'hooks/useAuth';
import dayjs from 'dayjs';
import { routes } from '../../../config/config';
import { Products } from '../../Form/Listings/ListingApplication/ApplicationSummary/Products';
import { Deliveries } from '../../Form/Listings/ListingApplication/ApplicationSummary/Deliveries';
import { DateProgress } from '../../DateProgress';
import { adminPath } from 'enums/adminRoutes';
import {
  GROUPED_QUALITY_MARKS_URL,
  QualityMarkOption,
  findQualityMark,
  hasRequiredQualityMark,
} from 'utils/qualityMarkHelpers';

interface TreeTableRow {
  id: number | string;
  applicant_farm_id?: number;
  producer_comment?: string;
  producer_comments?: string;
  offer_valid_from?: string;
  offer_valid_until?: string;
  warehouse_address?: string;
  delivery_radius?: number | string;
  delivery_conditions?: ApplicationDeliveryCondition[];
  status?: string;
}

interface ViewApplicationRecord {
  id: number | undefined;
  status: string | undefined;
  delivery_cost: number | undefined;
  total_offer_price: number | string | null | undefined;
  order_transaction: { id: number } | null | undefined;
  openProductsTab: boolean;
  applicant_farm_id?: number;
}

interface DisplayProduct {
  id: number | null;
  listing_product_id: string;
  offer_type: 'full_amount' | 'other_amount';
  farm_product_id: number | null;
  offered_quantity?: number;
  offered_unit_id?: number;
  offered_price?: number;
  offered_price_unit_id?: number;
  listingProduct: ListingProduct;
  selected_quantity?: number | null;
}

interface DrawerViewListingApplicationProps extends DrawerProps {
  application?:
    | TreeTableRow
    | ListingApplication
    | ViewApplicationRecord
    | null;
  listing?: Listing | null;
  applications?: ListingApplication[];
  onActionCompleted?: () => void;
  onRefetchSelectedProducts?: () => void;
  onRefetchApplication?: () => void;
  defaultActiveTab?: string;
  selectionMode?: boolean;
  selectedProductsData?: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>;
  selectProducts?: (data: {
    listing_id: string;
    selections: Array<{
      application_id: number;
      listing_application_product_id: number;
      selected_quantity: number;
    }>;
  }) => void;
}

export const DrawerViewListingApplication = ({
  open,
  onClose,
  closable = true,
  application,
  listing,
  applications = [],
  onActionCompleted,
  onRefetchSelectedProducts,
  onRefetchApplication,
  defaultActiveTab,
  selectionMode = false,
  selectedProductsData = [],
  selectProducts,
  zIndex,
}: DrawerViewListingApplicationProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (defaultActiveTab) return defaultActiveTab;
    return 'basic_info';
  });
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [messageDrawerOpen, setMessageDrawerOpen] = useState(false);
  const [withdrawDrawerOpen, setWithdrawDrawerOpen] = useState(false);

  const stableSelectedProductsData = useMemo(
    () => selectedProductsData || [],
    [JSON.stringify(selectedProductsData)]
  );

  const [allSelections, setAllSelections] = useState(
    stableSelectedProductsData
  );

  useEffect(() => {
    setAllSelections(stableSelectedProductsData);
  }, [stableSelectedProductsData]);

  const { user } = useAuth();
  const activeFarmId = user?.farmId;

  const applicationId = application?.id;
  const listingId =
    (application && 'listing_id' in application
      ? application.listing_id
      : null) || listing?.id;
  const {
    data: applicationData,
    isLoading: isLoadingApplication,
    isRefetching: isRefetchingApplication,
    refetch: refetchApplication,
  } = useQueryApiClient({
    request: {
      url: `/api/v2/listing-applications/${applicationId}`,
    },
    enabled: !!applicationId && open,
  });

  const { data: fullListingData, isLoading: isLoadingListing } =
    useQueryApiClient({
      request: {
        url: `/api/v2/listings/${listingId}`,
      },
      enabled: !!listingId && open,
    });

  const { appendData: performAction, isLoading: isActionLoading } =
    useQueryApiClient({
      request: {
        url: `/api/v2/listing-applications/:id/action`,
        method: 'POST',
      },
      enabled: !!applicationId,
      onSuccess: (response, passOnSuccess) => {
        const actionType = passOnSuccess?.action as string;

        const messageKey =
          actionType === 'accept'
            ? 'message.application_accepted'
            : actionType === 'withdraw'
              ? 'message.application_withdrawn'
              : actionType === 'reject'
                ? 'message.application_rejected'
                : 'message.application_selected';

        message.success(intl.formatMessage({ id: messageKey }));
        refetchApplication();
        onActionCompleted?.();

        if (['select', 'reject'].includes(actionType)) {
          onRefetchSelectedProducts?.();
        }
      },
      onError: (error) => {
        const errorMessage =
          error?.message ||
          error?.errors?.action?.[0] ||
          intl.formatMessage({ id: 'message.error' });
        message.error(errorMessage);
      },
      onFinally: () => setPendingAction(null),
    });

  const { appendData: createTransaction, isLoading: isCreatingTransaction } =
    useQueryApiClient({
      request: {
        url: '/api/v2/listing-applications/:id/create-transaction',
        method: 'POST',
      },
      onSuccess: (response: { transaction_id: number }) => {
        if (response?.transaction_id) {
          message.success(
            intl.formatMessage({ id: 'message.transaction_created' })
          );
          onClose?.({} as React.MouseEvent);
          onActionCompleted?.();
          navigate(
            `${adminPath.farmTransactions()}?tab=my_orders&transaction=${response.transaction_id}`
          );
        }
      },
      onError: (error) => {
        const errorMessage =
          error?.message || intl.formatMessage({ id: 'message.error' });
        message.error(errorMessage);
      },
    });

  const applicantFarmId =
    applicationData?.applicant_farm_id ?? application?.applicant_farm_id;

  const { data: farmData, isLoading: isLoadingFarm } = useQueryApiClient({
    request: {
      url: `api/v2/farms/${applicantFarmId}`,
    },
    enabled: !!applicantFarmId && open,
  });

  const { data: farmQualityMarks } = useQueryApiClient({
    request: {
      url: `api/v2/farms/qmarks/${applicantFarmId}`,
    },
    enabled: !!applicantFarmId && open,
  });

  const { data: allQualityMarks } = useQueryApiClient({
    request: {
      url: GROUPED_QUALITY_MARKS_URL,
    },
    enabled: open,
  });

  const categoryIds = useMemo(() => {
    const currentListing = fullListingData || listing;
    if (!applicationData?.application_products || !currentListing?.products)
      return [];
    const ids = new Set<number>();
    applicationData.application_products.forEach(
      (product: ApplicationProductData) => {
        if (product.farm_product_id) {
          const listingProduct = currentListing.products!.find(
            (lp: ListingProduct) => lp.id === product.listing_product_id
          );
          if (listingProduct?.category_id) {
            ids.add(listingProduct.category_id);
          }
        }
      }
    );
    return Array.from(ids);
  }, [applicationData?.application_products, fullListingData, listing]);

  const [categoryProductsData, setCategoryProductsData] = useState<{
    [categoryId: number]: FarmProduct[];
  }>({});

  const { appendData: fetchCategoryProductsBatch } = useQueryApiClient({
    request: {
      url: 'api/v2/categories/products/batch',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setCategoryProductsData(response || {});
    },
  });

  useEffect(() => {
    const currentListing = fullListingData || listing;
    if (!open || !currentListing || categoryIds.length === 0) {
      setCategoryProductsData({});
      return;
    }

    setCategoryProductsData({});

    const data = {
      category_ids: categoryIds,
      ...(user?.farmId && { farm_id: user.farmId }),
    };

    fetchCategoryProductsBatch(data);
  }, [open, fullListingData, listing, categoryIds, user?.farmId]);

  const availableProductsByCategory = categoryProductsData;

  const farmProductNames = useMemo(() => {
    let namesMap: { [key: number]: string } = {};

    Object.values(availableProductsByCategory).forEach((products) => {
      products.forEach((product) => {
        if (product.id && product.name) {
          namesMap[product.id] = product.name;
        }
      });
    });

    return namesMap;
  }, [availableProductsByCategory]);

  const isDataLoading =
    isLoadingFarm ||
    isLoadingApplication ||
    isRefetchingApplication ||
    isLoadingListing;
  const status = applicationData?.status || application?.status;
  const isSubmitted =
    status === 'submitted' || applicationData?.is_pending_submitting;
  const isSelected = status === 'selected';
  const isRejected = status === 'rejected';
  const currentListing = fullListingData || listing;
  const isOwner = currentListing?.farm_id === activeFarmId;
  const isApplicant = applicantFarmId === activeFarmId;
  const hasOtherSelected = useMemo(
    () =>
      applications.some(
        (app) => app.status === 'selected' && app.id !== applicationId
      ),
    [applications, applicationId]
  );

  const triggerAction = (
    action: 'select' | 'accept' | 'withdraw' | 'reject'
  ) => {
    if (!applicationId) return;

    setPendingAction(action);
    performAction({ action }, { id: applicationId }, { action });
  };

  const handleSelect = () => {
    if (selectionMode) {
      const currentAppSelections = allSelections.filter(
        (selection) => selection.listing_application_id === application?.id
      );

      if (currentAppSelections.length === 0) {
        message.warning(
          intl.formatMessage({
            id: 'listings.applications.no_products_selected',
          })
        );
        return;
      }

      const selections = allSelections.map((selection) => ({
        application_id:
          selection.listing_application_id ||
          (application?.id ? Number(application.id) : 0),
        listing_application_product_id:
          selection.listing_application_product_id,
        selected_quantity: selection.selected_quantity,
      }));

      let hasExceedingQuantity = false;
      const currentListing = fullListingData || listing;
      if (currentListing?.products) {
        const selectedQuantitiesByProduct: { [key: string]: number } = {};

        selections.forEach((selection) => {
          const app = applications.find(
            (a) => a.id === selection.application_id
          );
          const appProduct = app?.application_products?.find(
            (ap: ApplicationProductData) =>
              ap.id === selection.listing_application_product_id
          );
          const listingProductId = appProduct?.listing_product_id;

          if (listingProductId) {
            selectedQuantitiesByProduct[listingProductId] =
              (selectedQuantitiesByProduct[listingProductId] || 0) +
              selection.selected_quantity;
          }
        });

        hasExceedingQuantity = Object.entries(selectedQuantitiesByProduct).some(
          ([productId, selectedQuantity]) => {
            const listingProduct = currentListing.products?.find(
              (product: ListingProduct) => product.id === productId
            );
            return (
              listingProduct &&
              selectedQuantity > (listingProduct.required_quantity || 0)
            );
          }
        );
      }

      const makeRequest = () => {
        selectProducts?.({
          listing_id: listingId || '',
          selections,
        });

        setTimeout(() => {
          refetchApplication();
        }, 300);
      };

      if (hasExceedingQuantity) {
        Modal.confirm({
          title: intl.formatMessage({ id: 'general.confirm' }),
          content: intl.formatMessage({
            id: 'listings.applications.confirm_exceed_quantity',
          }),
          okText: intl.formatMessage({ id: 'general.yes' }),
          cancelText: intl.formatMessage({ id: 'general.no' }),
          onOk: makeRequest,
        });
      } else {
        makeRequest();
      }

      return;
    }

    if (hasOtherSelected) {
      Modal.confirm({
        title: intl.formatMessage({
          id: 'listings.applications.change_supplier_question',
        }),
        okText: intl.formatMessage({ id: 'general.yes' }),
        cancelText: intl.formatMessage({ id: 'general.no' }),
        onOk: () => triggerAction('select'),
      });

      return;
    }

    triggerAction('select');
  };

  const handleAccept = () => triggerAction('accept');

  const handleWithdraw = () => {
    setWithdrawDrawerOpen(true);
  };

  const handleOwnerReject = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'listings.applications.confirm_cancel_offer',
      }),
      okText: intl.formatMessage({ id: 'general.yes' }),
      cancelText: intl.formatMessage({ id: 'general.no' }),
      onOk: () => triggerAction('reject'),
    });
  };

  const totalDeliveryCost = Number(applicationData?.delivery_cost) || 0;

  const listingQualityMarks = useMemo(() => {
    const currentListing = fullListingData || listing;
    if (!currentListing?.products || !allQualityMarks) return [];

    const qualityMarksMap = new Map<string, QualityMarkOption>();

    currentListing.products.forEach((product: ListingProduct) => {
      if (product.quality_mark_ids && Array.isArray(product.quality_mark_ids)) {
        product.quality_mark_ids.forEach((id) => {
          const qm = findQualityMark<QualityMarkOption>(allQualityMarks, id);
          if (qm && !qualityMarksMap.has(String(id))) {
            qualityMarksMap.set(String(id), { id: qm.id, name: qm.name });
          }
        });
      }
    });

    return Array.from(qualityMarksMap.values());
  }, [fullListingData, listing, allQualityMarks]);

  const farmQualityMarkIds: QualityMarkId[] =
    farmQualityMarks?.map((qmark: FarmQualityMark) => qmark.value) || [];

  const qualityMarkOptions = useMemo(() => {
    return listingQualityMarks.map((qmark) => {
      const hasFarmQualityMark = hasRequiredQualityMark(
        qmark.id,
        farmQualityMarkIds,
        allQualityMarks
      );
      return {
        value: qmark.id,
        label: qmark.name,
        disabled: true,
        checked: true,
        className: hasFarmQualityMark ? 'has-quality-mark' : 'no-quality-mark',
      };
    });
  }, [listingQualityMarks, farmQualityMarkIds, allQualityMarks]);

  const certificates: ApplicationCertificate[] =
    applicationData?.certificates || [];

  const displayProducts = useMemo(() => {
    const currentListing = fullListingData || listing;
    if (!currentListing?.products || !applicationData?.application_products) {
      return [];
    }

    return applicationData.application_products
      .filter((ap: ApplicationProductData) => ap.listing_product_id)
      .map((ap: ApplicationProductData) => {
        const listingProduct = currentListing.products!.find(
          (lp: ListingProduct) => lp.id === ap.listing_product_id
        );

        const offerType =
          ap.offered_quantity != null && ap.offered_quantity !== undefined
            ? 'other_amount'
            : ap.offer_type || 'full_amount';

        return {
          id: ap.id,
          listing_product_id: ap.listing_product_id!,
          offer_type: offerType as 'full_amount' | 'other_amount',
          farm_product_id: ap.farm_product_id || null,
          offered_quantity: ap.offered_quantity,
          offered_unit_id:
            ap.offered_unit_id || listingProduct?.storage_unit?.id,
          offered_price: ap.offered_price,
          offered_price_unit_id:
            ap.offered_price_unit_id || listingProduct?.storage_unit?.id,
          listingProduct,
          selected_quantity:
            ap.selected_quantity ??
            ap.listing_product_selection?.selected_quantity ??
            null,
        };
      });
  }, [fullListingData, listing, applicationData?.application_products]);

  const handleProductSelectionChange = useCallback(
    (
      productId: string,
      selected: boolean,
      quantity: number,
      selectionType: 'full' | 'partial'
    ) => {
      setAllSelections((prevSelections) => {
        const product = displayProducts.find(
          (p: DisplayProduct) => p.listing_product_id === productId
        );

        if (!product) {
          return prevSelections;
        }

        const applicationProductId = Number(product.id);

        if (selected) {
          const newSelection = {
            listing_application_id: Number(application?.id) || 0,
            listing_application_product_id: applicationProductId,
            selected_quantity: quantity,
          };

          const existingIndex = prevSelections.findIndex(
            (sel) => sel.listing_application_product_id === applicationProductId
          );

          let newSelections;
          if (existingIndex >= 0) {
            newSelections = [...prevSelections];
            newSelections[existingIndex] = newSelection;
          } else {
            newSelections = [...prevSelections, newSelection];
          }
          return newSelections;
        } else {
          return prevSelections.filter(
            (sel) => sel.listing_application_product_id !== applicationProductId
          );
        }
      });
    },
    [displayProducts]
  );

  const drawerTitle = useMemo(() => {
    if (!farmData) {
      return null;
    }

    const farmName = farmData.brand || '';
    const regNr = farmData.reg_nr || '';
    const vatNr = farmData.pvn_nr || '';
    const address = farmData.registered_full_address || '';
    const contactPhone = farmData.contact_phone || '';
    const contactEmail = farmData.contact_email || '';

    return (
      <StyledDrawerTitleWrapper>
        <div className="title">
          <Label
            title={true}
            label={`${farmName} ${intl.formatMessage({
              id: 'listings.applications.offer',
            })}`}
          />
          {regNr && (
            <Label
              label={`${intl.formatMessage({
                id: 'contracts.reg_nr',
              })}: ${regNr}`}
            />
          )}
          {vatNr && (
            <Label
              label={`${intl.formatMessage({
                id: 'profile.reg_nr',
              })}: ${vatNr}`}
            />
          )}
          {address && (
            <Label
              label={`${intl.formatMessage({
                id: 'general.address',
              })}: ${address}`}
            />
          )}
          {contactPhone && (
            <Label
              label={`${intl.formatMessage({
                id: 'general.telephone',
              })}: ${contactPhone}`}
            />
          )}
          {contactEmail && (
            <Label
              label={`${intl.formatMessage({
                id: 'general.email',
              })}: ${contactEmail}`}
            />
          )}
        </div>
      </StyledDrawerTitleWrapper>
    );
  }, [farmData, intl]);

  const handleCertificateDownload = (cert: ApplicationCertificate) => {
    const fileObj = cert.file;

    if (fileObj?.url && cert.file_id && cert.listing_application_id) {
      const href = `${routes.api.baseUrl}/api/v2/listing-applications/${cert.listing_application_id}/certificates/download?file_id=${cert.file_id}`;
      window.open(href, '_blank');
      return;
    }

    if (fileObj?.url) {
      window.open(`${routes.api.baseUrl}/storage/${fileObj.url}`, '_blank');
    }
  };

  const tabs = useMemo(() => {
    return [
      {
        key: 'basic_info',
        label: intl.formatMessage({
          id: 'listings.application.basic_information',
        }),
        children: (
          <StyledApplicationForm>
            {isApplicant && (
              <div className="listing-info">
                <Label
                  label={
                    currentListing?.farm_name ||
                    currentListing?.farm_brand ||
                    ''
                  }
                  title={true}
                />
              </div>
            )}
            {/* Producer Comments */}
            <div className="producer-comments">
              <Label
                label={intl.formatMessage({
                  id: 'listings.application.producer_comment',
                })}
                title={true}
              />
              <p>{applicationData?.producer_comment || '-'}</p>
            </div>

            <Divider />

            {/* Offer Validity Period */}
            <div className="offer-validity-period">
              <Label
                label={intl.formatMessage({
                  id: 'listings.application.offer_validity_period',
                })}
                title={true}
              />
              <div className="row">
                <div className="date">
                  <DatePicker
                    name="offer_valid_from"
                    label={intl.formatMessage({
                      id: 'listings.application.start_date',
                    })}
                    initialValue={
                      applicationData?.offer_valid_from
                        ? dayjs(applicationData.offer_valid_from)
                        : undefined
                    }
                    readOnly={true}
                    format="DD.MM.YYYY HH:mm"
                    showTime={true}
                  />
                </div>
                <div className="date">
                  <DatePicker
                    name="offer_valid_until"
                    label={intl.formatMessage({
                      id: 'listings.application.end_date',
                    })}
                    initialValue={
                      applicationData?.offer_valid_until
                        ? dayjs(applicationData.offer_valid_until)
                        : undefined
                    }
                    readOnly={true}
                    format="DD.MM.YYYY"
                  />
                </div>
              </div>
            </div>

            <Divider />

            {/* Warehouse Address */}
            <div className="warehouse-address">
              <Label
                label={intl.formatMessage({
                  id: 'listings.application.warehouse_address',
                })}
                title={true}
              />
              <Input
                label={intl.formatMessage({ id: 'general.address' })}
                value={applicationData?.warehouse_address || ''}
                readOnly={true}
              />
            </div>

            {/* Delivery Radius */}
            <div>
              <Label
                label={intl.formatMessage({
                  id: 'listings.application.Delivery_radius',
                })}
                title={true}
              />
              <p>
                {currentListing?.delivery_radius_km
                  ? `${currentListing.delivery_radius_km} km`
                  : '-'}
              </p>
            </div>

            <Divider />

            {/* Total Delivery Cost */}
            <div className="total-delivery-cost">
              <Label
                label={intl.formatMessage({
                  id: 'listings.application.total_delivery_cost',
                })}
                title={true}
              />
              <Input
                label={intl.formatMessage({ id: 'general.EUR' })}
                value={totalDeliveryCost.toFixed(2)}
                readOnly={true}
              />
            </div>

            <Divider />

            {/* Quality Marks */}
            {qualityMarkOptions.length > 0 && (
              <div className="quality-marks">
                <Label
                  label={intl.formatMessage({
                    id: 'listings.application.quality_marks',
                  })}
                  title={true}
                />
                <div className="quality-mark-items">
                  {qualityMarkOptions.map((qmarkOption) => (
                    <div key={qmarkOption.value} className="quality-mark-item">
                      <Checkbox
                        checked={qmarkOption.checked}
                        disabled={qmarkOption.disabled}
                        className={qmarkOption.className}
                      />
                      <Label
                        className="quality-mark-label"
                        label={qmarkOption.label}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Certificates */}
            {certificates.length > 0 && (
              <div className="product-certificates">
                <StyledCustomUploadList>
                  {certificates.map((cert) => {
                    const fileObj = cert.file;
                    const name =
                      cert.file_name ||
                      fileObj?.original_name ||
                      fileObj?.name ||
                      '';
                    const extFromName = name?.includes('.')
                      ? name.split('.').pop()
                      : undefined;
                    const displayFormat = (
                      extFromName ||
                      cert.file_format ||
                      fileObj?.mime_type ||
                      ''
                    )
                      .toString()
                      .toUpperCase();
                    const displaySize =
                      cert.file_size || fileObj?.size_bytes || 0;
                    const uploadedAt = cert.created_at || fileObj?.created_at;

                    const onDownload = () => {
                      handleCertificateDownload(cert);
                    };

                    const fileUid = String(cert.id || Date.now());

                    const fileForCustomDocument: UploadFile = {
                      uid: fileUid,
                      name: name,
                      status: 'done',
                      file_size: displaySize,
                      file_format: displayFormat,
                      created_at: uploadedAt,
                    } as UploadFile;

                    return (
                      <CustomDocument
                        key={cert.id}
                        file={fileForCustomDocument}
                        onDownload={onDownload}
                        showActions={true}
                      />
                    );
                  })}
                </StyledCustomUploadList>
              </div>
            )}
          </StyledApplicationForm>
        ),
      },
      {
        key: 'products',
        label: intl.formatMessage({ id: 'listings.application.products' }),
        children: (
          <StyledApplicationSummary>
            <Products
              displayProducts={displayProducts}
              farmProductNames={farmProductNames}
              availableProductsByCategory={availableProductsByCategory}
              allQualityMarks={allQualityMarks}
              selectionMode={selectionMode}
              selectedProductsData={allSelections}
              onSelectionChange={handleProductSelectionChange}
            />
          </StyledApplicationSummary>
        ),
      },
      {
        key: 'delivery',
        label: intl.formatMessage({
          id: 'listings.application.delivery_conditions',
        }),
        children: (
          <StyledApplicationSummary>
            <Deliveries
              displayDeliveries={applicationData?.delivery_conditions || []}
              listingData={currentListing}
              warehouseAddress={applicationData?.warehouse_address}
              farmProductNames={farmProductNames}
              applicationProducts={applicationData?.application_products || []}
            />
          </StyledApplicationSummary>
        ),
      },
      {
        key: 'contract',
        label: intl.formatMessage({
          id: 'listings.application.contract_terms',
        }),
        children: (
          <StyledApplicationSummary>
            {/* Contract Period */}
            <div className="contract-period">
              <Label
                label={
                  intl.formatMessage({
                    id: 'listings.application.contract_start_date',
                  }) + ':'
                }
              />
              <div className="deadline-date">
                {currentListing?.contract_terms?.contract_start_date
                  ? dayjs(
                      currentListing.contract_terms.contract_start_date
                    ).format('DD.MM.YYYY')
                  : '---'}
              </div>
              {currentListing?.contract_terms?.contract_start_date &&
                currentListing?.contract_terms?.contract_end_date && (
                  <DateProgress
                    startDate={
                      currentListing.contract_terms.contract_start_date
                    }
                    endDate={currentListing.contract_terms.contract_end_date}
                  />
                )}
              <Label
                label={
                  intl.formatMessage({
                    id: 'listings.application.contract_end_date',
                  }) + ':'
                }
              />
              <div className="deadline-date">
                {currentListing?.contract_terms?.contract_end_date
                  ? dayjs(
                      currentListing.contract_terms.contract_end_date
                    ).format('DD.MM.YYYY')
                  : '---'}
              </div>
            </div>

            <Divider />

            {/* Payment Terms */}
            <div className="terms">
              <Label
                label={intl.formatMessage({
                  id: 'listings.application.payment_terms',
                })}
                title={true}
              />
              <p>
                {currentListing?.contract_terms?.payment_conditions || '---'}
              </p>
            </div>

            <Divider />

            {/* Additional Contract Terms */}
            <div className="terms">
              <Label
                label={intl.formatMessage({
                  id: 'listings.application.additional_contract_terms',
                })}
                title={true}
              />
              <p>
                {currentListing?.contract_terms?.additional_contract_terms ||
                  '---'}
              </p>
            </div>

            {/* Contract Template */}
            {currentListing?.contract_terms?.files &&
              currentListing.contract_terms.files.length > 0 && (
                <>
                  <Divider />
                  <Label
                    label={intl.formatMessage({
                      id: 'listings.application.contract_template',
                    })}
                    title={true}
                  />
                  <StyledCustomUploadList>
                    {currentListing.contract_terms.files.map(
                      (file: FileMeta) => {
                        const name = file.original_name || file.name || '';
                        const extFromName = name?.includes('.')
                          ? name.split('.').pop()
                          : undefined;
                        const displayFormat = (
                          extFromName ||
                          file.mime_type ||
                          ''
                        )
                          .toString()
                          .toUpperCase();
                        const displaySize = file.size_bytes || 0;

                        const fileForCustomDocument: UploadFile = {
                          uid: String(file.id),
                          name: name,
                          status: 'done',
                          file_size: displaySize,
                          file_format: displayFormat,
                          created_at: file.created_at,
                          fileId: file.id,
                        } as UploadFile;

                        const handleDownloadContractTemplate = () => {
                          if (currentListing?.id) {
                            const downloadUrl = file.id
                              ? `${routes.api.baseUrl}/api/v2/listings/${currentListing.id}/contract-terms/download?file_id=${file.id}`
                              : `${routes.api.baseUrl}/api/v2/listings/${currentListing.id}/contract-terms/download`;
                            window.open(downloadUrl, '_blank');
                          }
                        };

                        return (
                          <CustomDocument
                            key={file.id}
                            file={fileForCustomDocument}
                            onDownload={handleDownloadContractTemplate}
                            showActions={true}
                          />
                        );
                      }
                    )}
                  </StyledCustomUploadList>
                </>
              )}
          </StyledApplicationSummary>
        ),
      },
    ];
  }, [
    applicationData,
    fullListingData,
    listing,
    totalDeliveryCost,
    qualityMarkOptions,
    certificates,
    intl,
    displayProducts,
    farmProductNames,
    availableProductsByCategory,
    allQualityMarks,
  ]);

  const Footer = () => {
    const footerButtons = [
      <Button
        key="back"
        label={intl.formatMessage({ id: 'general.back' })}
        onClick={onClose}
        disabled={isActionLoading}
      />,
    ];

    const isAccepted = status === 'accepted';
    if (isAccepted && isOwner) {
      footerButtons.unshift(
        <Button
          key="other-applications"
          type="link"
          className="left"
          label={intl.formatMessage({
            id: 'listings.applications.other_unapproved_applications',
          })}
          onClick={() => {
            const listingId = currentListing?.id;
            if (listingId) {
              navigate(adminPath.listingApplications(listingId));
              onClose?.({} as React.MouseEvent);
            }
          }}
        />
      );

      footerButtons.push(
        <Button
          key="send-message"
          label={intl.formatMessage({ id: 'general.send_message' })}
          onClick={() => setMessageDrawerOpen(true)}
          disabled={isActionLoading}
        />
      );

      if (applicationData?.order_transaction?.id) {
        footerButtons.push(
          <Button
            key="view-transaction"
            label={intl.formatMessage({
              id: 'listings.applications.view_transaction',
            })}
            type="primary"
            onClick={() =>
              navigate(
                `${adminPath.farmTransactions()}?transaction=${applicationData.order_transaction!.id}`
              )
            }
          />
        );
      } else {
        footerButtons.push(
          <Button
            key="create-transaction"
            label={intl.formatMessage({
              id: 'listings.applications.create_transaction',
            })}
            type="primary"
            onClick={() => createTransaction({}, { id: applicationId })}
            disabled={isActionLoading || isCreatingTransaction}
            loading={isCreatingTransaction}
          />
        );
      }
    }

    if (isOwner && (isSubmitted || isRejected)) {
      footerButtons.push(
        <Button
          key="select"
          label={intl.formatMessage({
            id: 'listings.applications.select_offer',
          })}
          type="primary"
          onClick={handleSelect}
          loading={pendingAction === 'select' && isActionLoading}
          disabled={
            !applicationId ||
            isActionLoading ||
            status === 'withdrawn' ||
            (listing?.status !== 'closed' &&
              listing?.status !== 'partially_completed')
          }
        />
      );
    }

    if (isApplicant && (isSelected || isSubmitted)) {
      footerButtons.push(
        <Button
          key="withdraw"
          label={intl.formatMessage({
            id: 'listings.applications.withdraw',
          })}
          onClick={handleWithdraw}
          danger={true}
          loading={pendingAction === 'withdraw' && isActionLoading}
          disabled={!applicationId || isActionLoading}
        />
      );
      if (isSelected) {
        footerButtons.push(
          <Button
            key="accept"
            label={intl.formatMessage({
              id: 'listings.applications.accept_offer',
            })}
            type="primary"
            onClick={handleAccept}
            loading={pendingAction === 'accept' && isActionLoading}
            disabled={!applicationId || isActionLoading}
          />
        );
      }
    }

    if (isOwner && isSelected) {
      footerButtons.push(
        <Button
          key="cancel-offer"
          label={intl.formatMessage({
            id: 'listings.applications.cancel_offer',
          })}
          onClick={handleOwnerReject}
          danger={true}
          loading={pendingAction === 'reject' && isActionLoading}
          disabled={!applicationId || isActionLoading}
        />
      );
    }

    return <StyledFooter>{footerButtons}</StyledFooter>;
  };

  return (
    <StyledDrawerViewListingApplication
      open={open}
      onClose={onClose}
      closable={closable}
      title={drawerTitle}
      width={1000}
      zIndex={zIndex}
      closeIcon={<Icon name={'global-close-icon'} type={'small'} />}
      destroyOnClose
      footer={<Footer />}
    >
      <Spinner spinning={isDataLoading} dontRender>
        {!isDataLoading && (
          <Form layout="vertical">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabs.map((tab) => ({
                key: tab.key,
                label: tab.label,
                children: tab.children,
              }))}
            />
          </Form>
        )}
      </Spinner>

      <DrawerSendMessage
        open={messageDrawerOpen}
        onClose={() => setMessageDrawerOpen(false)}
        context={{
          type: 'application',
          listingId: listingId || undefined,
          applicationId: applicationId ? Number(applicationId) : undefined,
          preselectedRecipients: applicationData?.applicant_farm_id
            ? [applicationData.applicant_farm_id]
            : [],
        }}
      />

      {applicationId && (
        <DrawerWithdrawListingApplication
          open={withdrawDrawerOpen}
          onClose={() => setWithdrawDrawerOpen(false)}
          applicationId={Number(applicationId)}
          onSuccess={() => {
            refetchApplication();
            onActionCompleted?.();
            onRefetchSelectedProducts?.();
          }}
        />
      )}
    </StyledDrawerViewListingApplication>
  );
};
